import {
  ProjectDetailsPageSettingsPageBudgetSectionFragment,
  ProjectManagementValueObjectsBudgetStrategyTypeEnum,
} from 'generated/resources/graphql'

import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'

import { IProjectBudgetValues } from 'modules/project_management/components/forms/ProjectBudgetStrategiesForm'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import { getMinutesFromDecimalHours } from 'modules/project_management/lib/formatHours'

export interface GetBudgetSectionMutationParams {
  project: ProjectDetailsPageSettingsPageBudgetSectionFragment
  data: IProjectBudgetValues
  getCurrentBudgetType: () => budgetTypesEnum
  createBudgetStrategy: (
    dto: {
      projectId: string
      budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
      plannedCents?: number
      plannedMinutes?: number
      feeAmountCents?: number
      fixedCostCents: number
      isBillable: boolean
      delegated: boolean
    },
    awaitRefetchQueries?: boolean
  ) => Promise<UnhandledResult<Result<unknown>>>
  updateBudgetStrategy: (
    dto: {
      id: string
      budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
      plannedCents?: number
      plannedMinutes?: number
      feeAmountCents?: number
      fixedCostCents: number
      isBillable: boolean
    },
    awaitRefetchQueries?: boolean
  ) => Promise<UnhandledResult<Result<unknown>>>
  deleteBudgetStrategy: (params: { id: string }) => Promise<UnhandledResult<Result<unknown>>>
  updateFinancialDetailsProject: (params: {
    variables: {
      projectId: string
      fixedCostCents?: number
      isBillable: boolean
      legalEntityId: string
    }
  }) => Promise<UnhandledResult<Result<unknown>>>
  hasSubprojectsFeature: boolean
}

export function getBudgetSectionMutation(
  params: GetBudgetSectionMutationParams
): Promise<UnhandledResult<Result<unknown>>> {
  const {
    project,
    data,
    getCurrentBudgetType,
    createBudgetStrategy,
    updateBudgetStrategy,
    deleteBudgetStrategy,
    updateFinancialDetailsProject,
  } = params

  const currentBudgetType = getCurrentBudgetType()
  const targetBudgetType = data.budgetType

  const currentDelegation = project.budgetStrategy?.delegated ?? false
  const targetDelegation = data.delegated

  let plannedCents: number | undefined
  const plannedCentsNumber = Number(data.plannedCents)
  if (
    targetBudgetType === budgetTypesEnum.withoutBudget ||
    !Number.isFinite(plannedCentsNumber) ||
    plannedCentsNumber === 0 ||
    targetDelegation
  ) {
    plannedCents = undefined
  } else {
    plannedCents = Math.round(plannedCentsNumber * 100)
  }

  let plannedMinutes: number | undefined
  const plannedMinutesNumber = Number(data.plannedMinutes)
  if (
    targetBudgetType !== budgetTypesEnum.totalBudget ||
    !Number.isFinite(plannedMinutesNumber) ||
    plannedMinutesNumber === 0 ||
    targetDelegation
  ) {
    plannedMinutes = undefined
  } else {
    plannedMinutes = getMinutesFromDecimalHours(plannedMinutesNumber)
  }

  const billable = data.isBillable && !targetDelegation

  let feeAmountCents: number | undefined
  const numericFeeAmountCents = Number(data.feeAmountCents)
  if (
    data.feeAmountCents == null ||
    !Number.isFinite(numericFeeAmountCents) ||
    numericFeeAmountCents === 0 ||
    targetDelegation ||
    targetBudgetType === budgetTypesEnum.withoutBudget ||
    !billable
  ) {
    feeAmountCents = undefined
  } else {
    feeAmountCents = Math.round(numericFeeAmountCents * 100)
  }

  let fixedCostCents: number
  if (data.fixedCostCents) {
    fixedCostCents = Math.round(Number(data.fixedCostCents) * 100)
  } else {
    fixedCostCents = 0
  }

  // Handle withoutBudget: delete strategy if exists, then update project (fixedCostCents, isBillable) when feature enabled
  if (targetBudgetType === budgetTypesEnum.withoutBudget && data.delegated === false) {
    const updateProjectFinancialDetails = () =>
      updateFinancialDetailsProject({
        variables: {
          projectId: project.id,
          fixedCostCents,
          isBillable: billable,
          legalEntityId: project.legalEntity.id,
        },
      })

    if (!project.budgetStrategy) {
      // Already without budget: only update project financial details when feature enabled
      return updateProjectFinancialDetails()
    }

    const deletePromise = deleteBudgetStrategy({ id: project.budgetStrategy.id })
    return deletePromise.then((deleteResult) => {
      const deleteHandled = handleResult(deleteResult, () => {})
      if (!deleteHandled.success) {
        return deleteResult
      }
      return updateProjectFinancialDetails()
    })
  }

  // Handle updates (same type) - fixed and total budget only
  if (currentBudgetType === targetBudgetType && currentDelegation === targetDelegation) {
    const budgetStrategy = project.budgetStrategy
    if (!budgetStrategy) {
      throw new Error('No budget strategy to update')
    }
    if (
      targetBudgetType !== budgetTypesEnum.projectFixedCost &&
      targetBudgetType !== budgetTypesEnum.totalBudget
    ) {
      throw new Error('Cannot update this budget type')
    }
    const validBudgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum =
      targetBudgetType === budgetTypesEnum.projectFixedCost ? 'project_fixed_cost' : 'total_budget'

    return updateBudgetStrategy(
      {
        id: budgetStrategy.id,
        budgetStrategyType: validBudgetStrategyType,
        plannedCents,
        plannedMinutes,
        feeAmountCents,
        fixedCostCents,
        isBillable: billable,
      },
      true
    )
  }

  // Handle creation/switching between types
  const getBudgetStrategyType = (): ProjectManagementValueObjectsBudgetStrategyTypeEnum => {
    if (targetBudgetType === budgetTypesEnum.projectFixedCost) return 'project_fixed_cost'
    if (targetBudgetType === budgetTypesEnum.totalBudget) return 'total_budget'
    if (targetBudgetType === budgetTypesEnum.withoutBudget) return 'without_budget'
    throw new Error(`Unknown budget type: ${targetBudgetType}`)
  }

  if (
    targetBudgetType !== budgetTypesEnum.projectFixedCost &&
    targetBudgetType !== budgetTypesEnum.totalBudget &&
    data.delegated === false
  ) {
    throw new Error(`Unknown budget type: ${targetBudgetType}`)
  }

  const selectedBudget = getBudgetStrategyType()
  return createBudgetStrategy(
    {
      projectId: project.id,
      budgetStrategyType: selectedBudget,
      plannedCents,
      plannedMinutes,
      feeAmountCents,
      fixedCostCents,
      isBillable: billable,
      delegated: targetDelegation,
    },
    true
  )
}
